import { HStack } from 'native-base';
import React, { ReactNode } from 'react';

import ReservationDetailDate from 'app/components/ReservationSummary/ReservationDetailDate';
import ReservationDetailOption from 'app/components/ReservationSummary/ReservationDetailOption';
import ReservationDetailTime from 'app/components/ReservationSummary/ReservationDetailTime';
import ReservationDetailTravelParty from 'app/components/ReservationSummary/ReservationDetailTravelParty';
import {
  EntitlementReservation,
  Pass,
  ReservationOption,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import { ReservationTextHelper } from 'app/services/I18n';
import { showReservationTime, ticketCategoryDateTime } from 'app/services/ReservationHelper';
import { tID } from 'app/services/TestHelper';
import { isAddonTicket } from 'app/services/TicketHelper';

export type ReservationDetailsProps = {
  children?: ReactNode;
  reservation: EntitlementReservation;
  passes: Pass[];
  rt: ReservationTextHelper;
  option?: ReservationOption;
  showOption?: boolean;
  showTime?: boolean;
  ticketCategory?: TicketCategory;
};

export default function ReservationDetails({
  reservation,
  passes,
  showOption,
  ticketCategory,
  option,
  rt,
}: ReservationDetailsProps) {
  const flexBasis = showOption ? '25%' : '33%';

  // Calculate add-ons quantity from reservation tickets
  const addOnsQuantity = reservation.tickets?.filter((ticket) => isAddonTicket(ticket)).length ?? 0;

  // Calculate each option's date/time based on the ticket category. Else default to reservation's date/time.
  const categoryDateTime = ticketCategoryDateTime(reservation, ticketCategory);
  const rowDateTime = categoryDateTime ?? reservation.reservationDateTime;
  const preArrivalLabel = rt('pre_arrival_label');
  // Show boarding times only when BOTH the payload time and the Hygraph label exist.
  const showPreArrival = !!reservation.preArrivalTime && !!preArrivalLabel && !categoryDateTime;
  const timeDateTime = showPreArrival ? reservation.preArrivalTime : rowDateTime;
  const timeLabel = showPreArrival ? preArrivalLabel : undefined;

  return (
    <HStack justifyContent="space-evenly" testID={tID('ReservationDetails')} w="full">
      <ReservationDetailTravelParty
        addOnsQuantity={addOnsQuantity}
        flexBasis={flexBasis}
        passes={passes}
      />
      {showOption && option && (
        <ReservationDetailOption flexBasis={flexBasis} option={option} rt={rt} />
      )}
      <ReservationDetailDate dateTime={rowDateTime} flexBasis={flexBasis} />
      {showReservationTime(reservation, ticketCategory) && (
        <ReservationDetailTime
          dateTime={timeDateTime}
          flexBasis={flexBasis}
          ianaTimezone={reservation.attractionConfig?.ianaTimezone}
          label={timeLabel}
        />
      )}
    </HStack>
  );
}
